import type { ProfilesData } from "../../store/useAuthStore";
import { parseLink } from "./parseLink";
import type { ParsedLink } from "./types";

export function resolveDeepLinkExamId(
  parsed: ParsedLink,
  currentExams?: { examId: number; name: string }[],
): number | string | undefined {
  if (typeof parsed.examName === "string" && parsed.examName) {
    return (currentExams ?? []).find((e) => e.name === parsed.examName)?.examId;
  }
  return parsed.examName;
}

export function isDeepLinkProfileSame(
  parsed: ParsedLink,
  currentGrade?: number,
  currentExams?: { examId: number; name: string }[],
): boolean {
  if (!parsed.grade || parsed.examName == null) return true;
  const gradeId = Number(parsed.grade);
  if (gradeId !== currentGrade) return false;
  return typeof parsed.examName === "number"
    ? (currentExams ?? []).some((e) => e.examId === parsed.examName)
    : (currentExams ?? []).some((e) => e.name === parsed.examName);
}

/** True when deeplink grade/exam differs from the signed-in profile (sync + new token required). */
export function needsDeepLinkProfileSync(
  parsed: ParsedLink,
  profilesData: ProfilesData | null,
): boolean {
  if (!parsed.grade) return false;

  const currentGrade =
    profilesData?.unified_account_details?.user_account_details?.grade?.gradeId;
  const currentExams = profilesData?.unified_account_details?.profiles?.[0]
    ?.exams as { examId: number; name: string }[] | undefined;
  const resolvedExamId = resolveDeepLinkExamId(parsed, currentExams);

  if (resolvedExamId == null) return false;
  return !isDeepLinkProfileSame(parsed, currentGrade, currentExams);
}

/**
 * Dashboard URLs keep `?module=…` until DeepLinkHandler navigates away.
 * Block child routes during that window so APIs (my-programs, subscription-type-components)
 * do not run before redirect + fresh token.
 */
export function isPendingDashboardDeepLinkRoute(
  pathname: string,
  search: string,
  origin: string = window.location.origin,
): boolean {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  if (!normalizedPath.endsWith("/dashboard")) return false;

  const parsed = parseLink(`${origin}${pathname}${search}`);
  return parsed?.module != null;
}
